import {
  WORKSPACE_AGENT_DOCK_KEY_ARM_DELAY_MS,
  WORKSPACE_AGENT_DOCK_LOCAL_ESCAPE_ATTR,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { isWorkspaceAgentTextEntry } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentTextEntry';

export type WorkspaceAgentDockKeyState = {
  armAt: number;
  isApproveDisabled: boolean;
  now: number;
  onApprove: () => void;
  onCancel: () => void;
  onPickOption: ((index: number) => void) | undefined;
  optionCount: number;
};

const dockKeyTarget = (event: KeyboardEvent): HTMLElement | null =>
  event.target instanceof HTMLElement ? event.target : null;

const isEventOutsideDock = (event: KeyboardEvent, dock: HTMLElement) =>
  event.target instanceof Node && !dock.contains(event.target);

const isModifiedKey = (event: KeyboardEvent) => event.metaKey || event.ctrlKey;

/** A field with its own Escape (close Something else) goes first. */
const shouldLeaveEscapeToField = (event: KeyboardEvent, target: HTMLElement | null) =>
  isModifiedKey(event) || event.shiftKey || target?.closest(`[${WORKSPACE_AGENT_DOCK_LOCAL_ESCAPE_ATTR}]`) != null;

const shouldRearm = (now: number, armAt: number, inTextEntry: boolean) => now < armAt && !inTextEntry;

const shouldIgnoreChord = (event: KeyboardEvent) => event.repeat || event.isComposing || event.altKey;

const isPlainEnter = (event: KeyboardEvent) => event.key === 'Enter' && !event.shiftKey;

const isFocusedActivationControl = (target: HTMLElement | null) => target?.closest('button, a[href]') != null;

/** The option search owns Enter (it picks the highlighted row). Everywhere else Enter continues. */
const isComboboxField = (target: HTMLElement | null) => target?.closest('[role="combobox"]') != null;

const shouldLeaveEnterToControl = (event: KeyboardEvent, target: HTMLElement | null) =>
  !isModifiedKey(event) && (isFocusedActivationControl(target) || isComboboxField(target));

const applyEnter = (event: KeyboardEvent, target: HTMLElement | null, state: WorkspaceAgentDockKeyState) => {
  // Enter on a keyboard-focused button clicks that button. A pointer click does not focus a chip, so Enter still continues.
  // A text field submits on plain Enter; Shift+Enter never reaches here and stays a newline.
  if (shouldLeaveEnterToControl(event, target)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (!state.isApproveDisabled) {
    state.onApprove();
  }
};

const isDigitPickKey = (event: KeyboardEvent, inTextEntry: boolean) =>
  !inTextEntry && !isModifiedKey(event) && !event.shiftKey && /^[1-9]$/.test(event.key);

const applyDigitPick = (event: KeyboardEvent, inTextEntry: boolean, state: WorkspaceAgentDockKeyState) => {
  if (!state.onPickOption || !isDigitPickKey(event, inTextEntry)) {
    return;
  }

  const index = Number(event.key) - 1;

  if (index < state.optionCount) {
    event.preventDefault();
    event.stopPropagation();
    state.onPickOption(index);
  }
};

/**
 * Document-level keys for a consent dock. Returns the next arm time: a key that lands outside a text
 * field in the first moment was still meant for the composer, so it is swallowed and the window restarts.
 * Typing in a field is the answer, so it must not keep postponing Enter.
 */
export const applyWorkspaceAgentDockKey = (
  event: KeyboardEvent,
  dock: HTMLElement,
  state: WorkspaceAgentDockKeyState,
): number => {
  if (event.defaultPrevented || isEventOutsideDock(event, dock)) {
    return state.armAt;
  }

  const target = dockKeyTarget(event);
  const inTextEntry = target !== null && isWorkspaceAgentTextEntry(target);

  if (event.key === 'Escape') {
    if (!shouldLeaveEscapeToField(event, target)) {
      event.stopPropagation();
      state.onCancel();
    }

    return state.armAt;
  }

  if (shouldRearm(state.now, state.armAt, inTextEntry)) {
    return state.now + WORKSPACE_AGENT_DOCK_KEY_ARM_DELAY_MS;
  }

  if (shouldIgnoreChord(event)) {
    return state.armAt;
  }

  if (isPlainEnter(event)) {
    applyEnter(event, target, state);
    return state.armAt;
  }

  applyDigitPick(event, inTextEntry, state);

  return state.armAt;
};
